import * as React from 'react'
import styled from 'styled-components'

import * as plume from '@ulule/owl-kit-components/next'

import * as api from '@owl-nest/api-client/next'
import { t, tc, tn, tp } from '@owl-nest/localize'
import * as models from '@owl-nest/models'
import * as utils from '@owl-nest/utils'
import * as env from '@owl-nest/config'

export type MembershipOrderItemProps = {
  className?: string
  isSubscriber?: boolean
  project: api.Project
  subscription: api.Subscription
  subscribeUrl: string
  titleLimit?: number
}

function MembershipOrderItemComponent({
  className,
  isSubscriber,
  project,
  subscription,
  subscribeUrl,
  titleLimit,
}: MembershipOrderItemProps): React.ReactElement {
  const [isOpen, setIsOpen] = React.useState(false)
  const userConfig = env.useUserEnv()

  function onToggle(): void {
    setIsOpen(!isOpen)
  }

  const reward = subscription.reward as unknown as models.reward.Reward
  const description = models.reward.description(reward)
  const img = models.reward.image(reward)
  const isMobileDevice = plume.utils.isMobile()
  const stockAvailable = reward.stock_available
  const subscriptionFee = reward.price
  const rewardTitle = models.reward.title(reward)
  const visibleRewardTitle = titleLimit ? utils.text.truncate(rewardTitle, titleLimit) : rewardTitle
  const isRecurringDonation = models.subscription.isMembershipRecurringDonation(subscription, project)

  return (
    <MembershipOrderItemWrapper className={className}>
      <MembershipOrderItemMain>
        <MembershipOrderItemHeader>
          {img ? <MembershipOrderItemImage src={img} alt={rewardTitle} /> : <MembershipOrderItemImageDefault />}

          <MembershipOrderItemTitle>
            <plume.styles.heading.XXXS>{visibleRewardTitle}</plume.styles.heading.XXXS>

            {isSubscriber &&
              (isRecurringDonation ? (
                <>
                  {tp('Free with monthly donation', 'membership')}
                  {tc('[amount] / month', {
                    amount: (
                      <plume.LocaleMoneyDisplayer
                        amount={subscription.total}
                        currency={project.currency}
                        localeFormat={userConfig.lang}
                        rates={env.CURRENCIES_RATES}
                        targetCurrency={project.currency}
                      />
                    ),
                  })}
                </>
              ) : (
                <>
                  <plume.styles.copy.S>
                    {reward.is_free ? (
                      <>{tp('Free', 'membership')}</>
                    ) : (
                      <>
                        {tc('[amount] / month', {
                          amount: (
                            <plume.LocaleMoneyDisplayer
                              amount={subscriptionFee}
                              currency={project.currency}
                              localeFormat={userConfig.lang}
                              rates={env.CURRENCIES_RATES}
                              targetCurrency={project.currency}
                            />
                          ),
                        })}
                      </>
                    )}
                  </plume.styles.copy.S>
                  {reward.tax_deductible && (
                    <TaxDeduction>
                      <plume.glyphs.stroke.Percent size={16} />
                      {t('%(price)s after tax reduction', {
                        price: plume.accounting.localeMoney(reward.tax_deductible_price, {
                          currency: project.currency,
                          lang: userConfig.lang ?? env.DEFAULT_LOCALE,
                        }),
                      })}
                    </TaxDeduction>
                  )}
                </>
              ))}
          </MembershipOrderItemTitle>
        </MembershipOrderItemHeader>

        {/* Note : this span is to keep the grid column flow consistent */}
        {isSubscriber ? (
          <span></span>
        ) : (
          <plume.Link href={subscribeUrl}>
            {stockAvailable === 0 ? (
              <plume.RoundButton fitToParent={isMobileDevice} disabled={true}>
                {t('Sold out membership')}
              </plume.RoundButton>
            ) : (
              <plume.RoundButton fitToParent={isMobileDevice} inverted={reward.is_free}>
                {reward.is_free ? (
                  <>{t('Subscribe for free')}</>
                ) : (
                  <>
                    {tc('Subscribe for [price] / month', {
                      price: (
                        <plume.LocaleMoneyDisplayer
                          amount={reward.price}
                          currency={project.currency}
                          localeFormat={userConfig.lang}
                          rates={env.CURRENCIES_RATES}
                          targetCurrency={project.currency}
                        />
                      ),
                    })}
                  </>
                )}
              </plume.RoundButton>
            )}
          </plume.Link>
        )}

        <MembershipOrderItemToggle onClick={onToggle} $isToggled={isOpen} $isSubscriber={isSubscriber}>
          {isOpen ? t('Show less') : t('Show more')}

          <plume.glyphs.stroke.CaretDown size={12} />
        </MembershipOrderItemToggle>
      </MembershipOrderItemMain>

      <MembershipOrderItemExpand $isOpen={isOpen}>
        {/* Note : this span is to keep the grid column flow consistent */}
        <span></span>
        <MembershipOrderItemContent>
          <plume.styles.card.reward.CardDescription dangerouslySetInnerHTML={{ __html: description || '' }} />

          {!isMobileDevice && project.show_subscriptions_count && (
            <plume.Tag color="blue">
              <plume.glyphs.fill.Heart size={12} />
              {tn('%(count)d member', '%(count)d members', reward.stock_taken, { count: reward.stock_taken })}
            </plume.Tag>
          )}
        </MembershipOrderItemContent>

        {!isOpen && <span></span>}

        <plume.styles.card.reward.CardFooter>
          {isMobileDevice && project.show_subscriptions_count && (
            <plume.Tag color="blue">
              <plume.glyphs.fill.Heart size={12} />
              {tn('%(count)d member', '%(count)d members', reward.stock_taken, { count: reward.stock_taken })}
            </plume.Tag>
          )}

          {!isSubscriber && (stockAvailable ?? 0) > 0 && (
            <MembershipOrderItemSubsRemaining>
              <plume.styles.heading.XXXXXS>
                {t('%(amount)d remaining', { amount: stockAvailable })}
              </plume.styles.heading.XXXXXS>

              {stockAvailable !== null && stockAvailable <= 5 && (
                <plume.styles.heading.XXXXXS>{t('Soon sold out!')}</plume.styles.heading.XXXXXS>
              )}
            </MembershipOrderItemSubsRemaining>
          )}
        </plume.styles.card.reward.CardFooter>
      </MembershipOrderItemExpand>
    </MembershipOrderItemWrapper>
  )
}

export const MembershipOrderItem = styled(MembershipOrderItemComponent)``

const MembershipOrderItemWrapper = styled(plume.styles.card.reward.CardWrapper)`
  gap: 0;
  margin-inline: auto;
  max-width: 100%;
  width: 100%;
`

const MembershipOrderItemImage = styled(plume.styles.card.reward.CardImage)`
  height: 48px;
  width: 90px;
`

const MembershipOrderItemImageDefault = styled(plume.DefaultRewardImage)`
  background-repeat: repeat;
  background-size: 31px;
  height: 48px;
  width: 90px;
`

const MembershipOrderItemContent = styled(plume.styles.card.reward.CardContent)`
  gap: 16px;
`

const MembershipOrderItemHeader = styled.div`
  align-items: center;
  display: flex;
  gap: 16px;
  margin-bottom: 8px;

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    margin-bottom: 0;
  }
`

const MembershipOrderItemTitle = styled.div`
  display: flex;
  flex-direction: column;
  text-align: left;
  width: fit-content;

  ${plume.styles.heading.XXXS} {
    max-width: 150px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    margin-right: 8px;

    ${plume.styles.heading.XXXS} {
      max-width: 260px;
    }
  }
`

const MembershipOrderItemMain = styled.div`
  align-items: center;
  display: grid;
  gap: 16px;

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    grid-template-columns: auto auto 1fr;
    max-width: 100%;
    min-height: 48px;
    row-gap: 0;
  }
`

const MembershipOrderItemExpand = styled.div<{ $isOpen: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ $isOpen }) => ($isOpen ? '16px' : '0')};
  transition: gap 0.15s ease-in-out;

  & > * {
    max-height: ${({ $isOpen }) => ($isOpen ? '100%' : '0')};
    overflow: hidden;
    transition: max-height 0.15s ease-in-out;
    transition: visibility 0.15s ease-in-out;
    visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
  }

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    display: grid;
    grid-template-columns: 90px auto auto;
    grid-template-rows: ${({ $isOpen }) => ($isOpen ? '1fr' : '0fr')};
    padding-top: ${({ $isOpen }) => ($isOpen ? '16px' : '0')};
    transition:
      padding-top 0.125s ease-in-out,
      grid-template-rows 0.15s ease-in-out;
  }
`

const MembershipOrderItemToggle = styled(plume.styles.toggle.ToggleButton)<{
  $isToggled: boolean
  $isSubscriber?: boolean
}>`
  color: ${plume.COLORS.PRIMARY_GREY_900};
  justify-self: center;
  margin-bottom: ${({ $isSubscriber }) => ($isSubscriber ? '-10px' : '0')};
  margin-top: ${({ $isSubscriber }) => ($isSubscriber ? '-32px' : '0')};
  white-space: nowrap;

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    justify-self: flex-end;
    margin-block: 0;
  }

  ${plume.glyphs.stroke.CaretDown} {
    color: ${plume.COLORS.PRIMARY_SAND_500};
  }
`

const MembershipOrderItemSubsRemaining = styled(plume.styles.card.reward.CardSubsRemaining)`
  justify-content: flex-end;
  margin-right: 8px;
  margin-bottom: 8px;
`

const TaxDeduction = styled(plume.styles.copy.S)`
  align-items: center;
  align-self: flex-start;
  color: ${plume.COLORS.PRIMARY_SAND_500};
  display: flex;
  gap: 8px;
`
